import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim(), origin_url: window.location.origin });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] nerd-gradient flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md animate-fade-up">
        <div className="bg-nerd-card border border-black/8 rounded-3xl p-8 shadow-2xl">
          <div className="w-11 h-11 rounded-2xl bg-nerd-light flex items-center justify-center mb-6">
            {sent ? <MailCheck className="w-5 h-5 text-white" /> : <Sparkles className="w-5 h-5 text-white" />}
          </div>
          {sent ? (
            <>
              <h1 className="font-display text-3xl font-extrabold tracking-tight mb-1">Check your inbox</h1>
              <p className="text-nerd-light/50 text-sm mb-7 leading-relaxed">
                If an account exists for <span className="font-semibold text-nerd-light/80">{email.trim()}</span>, we've sent a link to reset your password. The link expires in 1 hour.
              </p>
              <Link to="/login" data-testid="back-to-login"
                className="w-full inline-flex items-center justify-center gap-2 bg-nerd-light text-white font-semibold px-5 py-3 rounded-xl hover:opacity-90 transition-opacity">
                Back to log in
              </Link>
            </>
          ) : (
            <>
              <h1 className="font-display text-3xl font-extrabold tracking-tight mb-1">Forgot password?</h1>
              <p className="text-nerd-light/50 text-sm mb-7">Enter your email and we'll send you a reset link.</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-xs font-mono-x uppercase tracking-widest text-nerd-light/50 mb-1.5 block">Email</label>
                  <input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-nerd-depth border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-mint transition-colors" placeholder="you@email.com" />
                </div>
                <button data-testid="forgot-submit" disabled={loading} type="submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-nerd-light text-white font-semibold px-5 py-3 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
                </button>
              </form>
              <p className="text-sm text-nerd-light/50 mt-6 text-center">
                Remembered it? <Link to="/login" data-testid="go-login" className="text-nerd-light hover:underline">Log in</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
